import React from 'react';
import { ArrowRight } from '@phosphor-icons/react';
import { defineMessages, useIntl } from 'react-intl';
import { useData } from 'vike-react/useData';
import { usePageContext } from 'vike-react/usePageContext';
import { getVikeDomPageContextData } from '../../utils/vike/get-vike-dom-page-context-data';
import { getVikeSerializedPageData } from '../../utils/vike/get-vike-serialized-page-data';
import { Byline } from '../../design-system/ui/Byline/Byline';
import { Breadcrumbs } from '../../design-system/ui/Breadcrumbs/Breadcrumbs';
import { Card } from '../../design-system/ui/Card/Card';
import { Icon } from '../../design-system/ui/Icon';
import { PageContainer } from '../../design-system/ui/PageContainer/PageContainer';
import { PageHeader } from '../../design-system/ui/PageHeader/PageHeader';
import type { Data } from './+data.server';

const messages = defineMessages({
  home: {
    id: 'navigation.home.breadcrumb',
    defaultMessage: 'Home',
    description: 'Breadcrumb link to home page',
  },
  breadcrumbLabel: {
    id: 'breadcrumb.navigation',
    defaultMessage: 'Breadcrumb navigation',
    description: 'Accessibility label for breadcrumb navigation showing page hierarchy',
  },
  title: {
    id: 'authors.index.title',
    defaultMessage: 'Authors',
    description: 'Heading and breadcrumb of the authors index page (the people who write the JobCrawls guides)',
  },
  subtitle: {
    id: 'authors.index.subtitle',
    defaultMessage: "The people behind JobCrawls' research-led guides to the Finnish job market.",
    description: 'Intro line under the heading of the authors index page',
  },
  viewProfile: {
    id: 'authors.index.viewProfile',
    defaultMessage: 'View profile',
    description: 'Link on an author card in the authors index, opening that author page',
  },
});

export default function AuthorsIndexPage(): React.JSX.Element | null {
  const intl = useIntl();
  const dataFromHook = useData<Data>();
  const pageContext = usePageContext();
  const dataFromContext = (pageContext as { data?: Data } | undefined)?.data;
  const authors = (
    dataFromHook ??
    dataFromContext ??
    getVikeSerializedPageData<Data>() ??
    getVikeDomPageContextData<Data>()
  )?.authors;

  if (!authors) return null;

  const prefix = `/${intl.locale}`;

  return (
    <div className="min-h-screen bg-jc-page">
      <PageContainer width="prose">
        <div className="flex flex-col gap-jc-block py-jc-section">
          <PageHeader
            breadcrumbs={
              <Breadcrumbs
                label={intl.formatMessage(messages.breadcrumbLabel)}
                home={{ href: prefix, label: intl.formatMessage(messages.home) }}
                items={[{ label: intl.formatMessage(messages.title) }]}
              />
            }
            title={intl.formatMessage(messages.title)}
            subtitle={intl.formatMessage(messages.subtitle)}
          />

          <div className="flex flex-col gap-5">
            {authors.map((author) => (
              <Card as="article" key={author.slug} className="flex flex-col gap-4">
                <Byline
                  author={{
                    name: author.displayName,
                    href: `${prefix}/authors/${author.slug}`,
                    avatarSrc: author.headshotPath,
                    role: author.jobTitle,
                  }}
                />
                <p className="text-base text-jc-ink-secondary">{author.bio}</p>
                <a
                  href={`${prefix}/authors/${author.slug}`}
                  className="inline-flex items-center gap-1 self-start rounded-jc-info font-semibold text-jc-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus"
                >
                  {intl.formatMessage(messages.viewProfile)}
                  <Icon icon={ArrowRight} size="sm" />
                </a>
              </Card>
            ))}
          </div>
        </div>
      </PageContainer>
    </div>
  );
}
